import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GalleryDto, TenantDto } from '@nusign/picflow-backend-types';
import { getFormControl } from '../../../lib/get-form-control';
import { getFullPath } from '../../../lib/get-full-path';
import { GuestAuthService } from '../../../services/guest-auth.service';
import { StateService } from '../../../services/state.service';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { InputPreDirective } from '../../../shared-extra/directives/input-pre.directive';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { AutofocusDirective } from '../../../shared/directives/autofocus.directive';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { GalleryTeaserUrlPipe } from '../../../shared/pipes/gallery-teaser-url.pipe';
import { PreviewBannerComponent } from '../../components/preview-banner/preview-banner.component';
import { TenantIconLoaderComponent } from '../../components/tenant-icon-loader/tenant-icon-loader.component';

@Component({
  selector: 'app-gallery-identify-page',
  templateUrl: './gallery-identify-page.component.html',
  styleUrls: ['./gallery-identify-page.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SvgComponent,
    FormsModule,
    ReactiveFormsModule,
    InputFieldComponent,
    InputPreDirective,
    ButtonDirective,
    LoadingIndicatorComponent,
    TenantIconLoaderComponent,
    GalleryTeaserUrlPipe,
    AutofocusDirective,
    PreviewBannerComponent,
    TooltipDirective,
  ],
})
export class GalleryIdentifyPageComponent implements OnInit {
  private platformId = inject(PLATFORM_ID);
  private guestAuthService = inject(GuestAuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private state = inject(StateService);

  gallery: GalleryDto = this.route.snapshot.data.gallery;
  tenant = this.gallery?.tenant as TenantDto;
  form = new FormGroup({
    name: getFormControl('name'),
    email: getFormControl('email'),
  });
  loading = signal(false);
  platformBrowser = isPlatformBrowser(this.platformId);
  previewMode: boolean = this.state.galleryPreviewMode;

  ngOnInit(): void {
    if (this.previewMode) {
      this.form.patchValue({
        name: 'Visitor Name',
        email: 'visitorname@email.com',
      });
      this.form.disable();
    }
  }

  async submit() {
    if (!this.form.valid && !this.previewMode) return;
    if (this.loading()) return;
    try {
      this.loading.set(true);
      const resp = this.previewMode
        ? await this.guestAuthService.authenticateWithPreviewMode()
        : await this.guestAuthService.authenticateWithEmail(
            this.form.value.name,
            this.form.value.email,
            undefined,
            this.gallery
          );
      if (resp) {
        const path = getFullPath(this.route.snapshot);
        this.router.navigate(
          path.filter(p => p !== 'identify'),
          { queryParamsHandling: 'preserve' }
        );
      } else {
        this.loading.set(false);
      }
    } catch (err) {
      this.loading.set(false);
      throw err;
    }
  }
}
